import { useUserStore, usePageStore } from '@/store';
import { mapState as rootMapState } from 'pinia';
import { defineAsyncComponent } from 'vue';
import util from '../../common/util';
import { Preview, PreviewNum, DEFAULT_HOUSE_PREVIEW } from '@/common/utils';
import HouseFormat, {
    mapHouseData,
    getBasic,
    getProviderText,
    getFlagtext
} from '@/common/house';
import NoDisclosure from '../../components/nodisclosure.vue';
import detailPop from '@/components/global/detail-pop/index.js';
import HouseMask from './mask.vue';
import TitleMask from './_component/title-mask.js';
import { getHouseSchemaInfo } from '@/common/schemaInfo.js';
import signLog from '@/components/sign-log';
import houseTag from './_component/house-tag';
import { buildUrl, getRouteOptions } from '@/router/index.js';
export default {
    i18next: {
        ns: 'md-house'
    },
    props: {
        house: {
            type: Object
        },
        dir: {
            type: String,
            default: () => 'width'
        }
    },
    components: {
        NoDisclosure,
        imgSwiper: defineAsyncComponent(() =>
            import(
                /* webpackChunkName: "common/img-swiper" */ '@/components/img-swiper.vue'
            )),
        HouseMask,
        TitleMask,
        houseTag
    },
    data: function() {
        return {
            defHouseImg: DEFAULT_HOUSE_PREVIEW,
            previewIndex: 1,
            vowAddress: ''
            // provided: ''
        };
    },
    computed: {
        ...rootMapState(usePageStore, {
            currencySymbol: store => store.currency.symbol,
            currencyAbbr: store => store.currency.abbr,
            listingSearchSwitch: store => store.page.listingSearchSwitch,
            page: 'processedGlobalData',
            forCMS: 'forCMS'
        }),
        ...rootMapState(useUserStore, {
            loginStatus: store => store.loginStatus,
            user: store => store.user,
            userEmailVerified: 'userEmailVerified'
        }),
        houseData({ house, page }) {
            return HouseFormat.call(this, house, page);
        },
        ...mapHouseData('houseData'),
        provided({ house, listingSearchSwitch }) {
            return listingSearchSwitch
                ? house.mlsOrg?.cardTemplate
                : getProviderText(house.mlsOrg?.cardTemplate, house);
        },
        flagtext({ page }) {
            return getFlagtext.call(this, {
                house: this.house,
                fmtData: this,
                globalData: page
            });
        },
        basic() {
            let bsc = getBasic.call(this, this.house);
            if (this.isShowMask) {
                bsc = bsc.map(item => {
                    return {
                        key: item.key,
                        text: item.text.replace(/[\d|.|,]{1,}/g, '***')
                    };
                });
            }
            return bsc;
        },
        seoSchema({ page }) {
            return getHouseSchemaInfo(this.house, {
                url: this.detailUrl,
                country: page.country,
                location: this.pageLocation,
                currencyAbbr: this.currencyAbbr,
                agentCreator: page.agentCreator
            });
        },
        isMobile() {
            return this.platform.isMobile;
        },
        wordPressSSR({ page }) {
            return page.wordPressSSR;
        },
        widget({ page }) {
            return page.widget;
        },
        previews() {
            return this.previewPictures?.length > 0
                ? this.previewPictures
                : [this.preview];
        },
        previewsJson() {
            return JSON.stringify(this.previews);
        },
        showAllPrice({ showListingPrice, showSoldPrice }) {
            return showListingPrice && showSoldPrice;
        },
        hidePrice({ showListingPrice, showSoldPrice, soldDate }) {
            return !showListingPrice && !showSoldPrice && soldDate;
        },
        inPopDetailPage() {
            return util.inPopDetailPage;
        },
        linkData() {
            let link = this.link;
            if (this.isShowMask) {
                // changed real address to 'address'
                link = this.getVowDetailLink(link);
            } else if (this.house?.addressPartialFlag === 1) {
                // changed real address to 'Address-not-disclosed'
                link = this.getVowDetailLink(link, 'Address-not-disclosed');
            }
            return {
                href: this.transformLink2WordPress(link),
                target: this.detailUrlTarget,
                'aria-label': this.address,
                self: this.inPopDetailPage
            };
        },
        mlsAlt() {
            return (this.house.mlsOrg && this.house.mlsOrg.name) || this.mlsid;
        },
        isShowMask({ userEmailVerified, noVow }) {
            return this.isProtected && !userEmailVerified && !noVow;
        },
        noVow({ page }) {
            return page.pageKey === 'agent-detail';
        },
        showSwitch({ page }) {
            let { switchNeedSign } = page; // Whether to switch property photos left or right after logging in
            return Boolean(switchNeedSign !== true || this.user);
        },
        exclusiveTag({ exclusive, isShowMask }) {
            if (exclusive && !isShowMask) {
                return {
                    className: exclusive?.style?.className,
                    value: exclusive?.value || ''
                };
            }
            return false;
        },
        tag({ listingTags, status, isShowMask }) {
            if ((listingTags?.length || status) && !isShowMask) {
                return {
                    className: listingTags[0]?.style?.className || '',
                    value: listingTags[0]?.value || status
                };
            }
            return false;
        },
        moreListing() {
            if (this.moreInfo) {
                return this.moreInfo();
            }
        },
        isMobileSize() {
            return this.platform.isMobile;
        }
    },
    inject: {
        collectHandler: {
            type: Function
        },
        isInMap: {
            type: Boolean,
            default: false
        },
        moreInfo: {
            type: Function,
            default: null
        }
    },
    watch: {
        loginStatus(val) {
            if (val === -1) {
                this.collected = this.house.collectStatus = false;
                this.maskData();
            } else if (val === 1 && this.isShowMask) {
                this.vowAddress = this.$t('addressNotDisclosed');
            }
        },
        vow() {
            this.maskData();
        },
        ['house.' + Preview.origin](preview) {
            this[Preview.name] = preview;
        },
        ['house.' + PreviewNum.origin](preview) {
            this[PreviewNum.name] = preview;
        }
    },
    methods: {
        transformLink2WordPress(link) {
            return util.transformLink2WordPress(
                link,
                this.wordPressSSR,
                this.noVow
            );
        },
        getVowDetailLink(link, addressReplaceStr = 'address') {
            // changed real address to 'address'
            let routeInfo = {};
            if (this.house?.listingStatus?.toLowerCase() === 'sold') {
                routeInfo = getRouteOptions({ name: 'sold-detail', url: link });
            } else {
                routeInfo = getRouteOptions({ name: 'listing-detail', url: link });
            }
            routeInfo.params.address = addressReplaceStr;
            if (routeInfo.name) {
                return buildUrl(routeInfo)
            }
            return link;
        },
        onMouseEnter() {},
        onMouseLeave() {},
        collect() {
            let link = this.link;
            if (this.isShowMask) {
                // changed real address to 'address'
                link = this.getVowDetailLink(link);
            } else if (this.house?.addressPartialFlag === 1) {
                // changed real address to 'Address-not-disclosed'
                link = this.getVowDetailLink(link, 'Address-not-disclosed');
            }
            var data = {
                listingId: this.id,
                listingDetail: this.transformLink2WordPress(link),
                status: this.collected ? 2 : 1,
                address: this.house?.address,
                listingUrl: this.transformLink2WordPress(link)
            };
            if (this.widget) {
                const replace = false;
                let url = util.setQueryField(
                    { fav: data.status },
                    data.listingDetail,
                    replace
                );
                window.open(url, '_blank');
            } else {
                if (this.collectHandler) {
                    this.collectHandler(data).then(() => {
                        this.collected = this.house.collectStatus =
                            !this.collected;
                    });
                }
            }
        },
        onPreviewChange(v) {
            this.previewIndex = v;
        },
        openDetail(link, e) {
            if (this.isShowMask) {
                link = this.getVowDetailLink(link);
            } else if (this.house?.addressPartialFlag === 1) {
                // changed real address to 'Address-not-disclosed'
                link = this.getVowDetailLink(link, 'Address-not-disclosed');
            }
            const curLink = this.transformLink2WordPress(link);
            if (this.isMobile) {
                e.preventDefault();
                e.stopPropagation();
                if (this.inPopDetailPage) {
                    window.parent.__curDetailPop &&
                        window.parent.__curDetailPop.setLink(curLink);
                } else {
                    let openAnimation = this.isInMap
                        ? 'bottomToUp'
                        : 'fadeInRight';
                    //  mobile terminal widget Fail detailPop jump
                    this.detailPopIns = this.widget
                        ? window.open(this.getWidgetUrl(curLink))
                        : detailPop.open(curLink, openAnimation);
                }
            }
        },
        getWidgetUrl(url) {
            if (/global_domain/.test(this.pageLocation.search)) {
                url =
                    url.indexOf('?') > -1
                        ? `${url}&global_domain=${this.page.domainName}`
                        : `${url}?global_domain=${this.page.domainName}`;
                return `https://${window.location.host}${url}`;
            } else {
                return url;
            }
        },
        maskData() {
            if (this.isShowMask) {
                this.vowAddress = this.$t('addressNotDisclosed');
            }
        },
        register(options) {
            let event = options && options.event;
            if (event) {
                event.stopPropagation();
                event.preventDefault();
            }
            signLog.register({
                allowClose: true,
                languageTextFn: {
                    agreementHtml: _ => this.$t('byContinuingYouAgree')
                },
                onregister: (_user, _inquiry) => {
                    return false;
                },
                onsuccess: () => {
                    return false; //  Do not pop up the default prompt box
                }
            });
        },
        getHouseTagValue() {
            return this.$t('md-house.soldOn', { soldDate: this.$ft.formatDate(this.soldDate, 'MM/dd/yyyy') })
        }
    },
    created() {
        this.maskData();
    }
};
